// src/components/SubjectQualificationModal.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Input from './ui/Input';
import Card from './ui/Card';
import AssetManager from '../services/AssetManager';

// Helper function to get subject by ID from the fetched subjects array
const getSubjectById = (subjects, id) => subjects.find(s => s.id === id);

const SubjectItem = ({ subject, onDragStart }) => {
    if (!subject) return null;
    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, subject.id)}
            className="p-1.5 mb-1 border rounded shadow-sm cursor-grab active:cursor-grabbing text-xs"
            style={{ backgroundColor: subject.color || '#f0f0f0' }}
            title={subject.name}
        >
            <span className="font-medium mix-blend-difference text-white filter brightness-200 contrast-200">
                {subject.name}
            </span>
        </div>
    );
};


function SubjectQualificationModal({
    isOpen,
    onClose,
    initialQualifiedSubjectIds = [],
    onSaveQualifications,
    teacherName
}) {
    // State for subjects and loading
    const [subjects, setSubjects] = useState([]);
    const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
    const [subjectsError, setSubjectsError] = useState(null);
    const assetManager = useMemo(() => AssetManager.getInstance(), []);

    // Existing state
    const [qualifiedIds, setQualifiedIds] = useState(new Set());
    const [availableSubjects, setAvailableSubjects] = useState([]);
    const [filterText, setFilterText] = useState('');
    const [draggedSubjectId, setDraggedSubjectId] = useState(null);
    const [dragOverContainer, setDragOverContainer] = useState(null);

    // Fetch subjects when component mounts or AssetManager changes
    useEffect(() => {
        const fetchSubjects = async () => {
            setIsLoadingSubjects(true);
            setSubjectsError(null);
            try {
                console.log('[SubjectQualificationModal] Fetching subjects...');
                const fetchedSubjects = await assetManager.fetchSubjects();
                console.log('[SubjectQualificationModal] Fetched subjects:', fetchedSubjects.length);
                setSubjects(fetchedSubjects);
            } catch (error) {
                console.error('[SubjectQualificationModal] Error fetching subjects:', error);
                setSubjectsError(error.message || 'Failed to load subjects');
                setSubjects([]); // Fallback to empty array
            } finally {
                setIsLoadingSubjects(false);
            }
        };

        fetchSubjects();
    }, [assetManager]);

    // Update available subjects when modal opens, subjects change, or initial qualifications change
    useEffect(() => {
        if (isOpen && subjects.length > 0) {
            const initialSet = new Set(initialQualifiedSubjectIds);
            setQualifiedIds(initialSet);
            setFilterText('');
            const available = subjects.filter(s => !initialSet.has(s.id))
                .sort((a, b) => a.name.localeCompare(b.name));
            setAvailableSubjects(available);
        }
    }, [isOpen, subjects, initialQualifiedSubjectIds]);

    const filteredAvailableSubjects = useMemo(() => {
        if (!filterText) return availableSubjects;
        return availableSubjects.filter(s =>
            s.name.toLowerCase().includes(filterText.toLowerCase())
        );
    }, [availableSubjects, filterText]);

    const qualifiedSubjectObjects = useMemo(() => {
        return Array.from(qualifiedIds)
            .map(id => getSubjectById(subjects, id))
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [qualifiedIds, subjects]);

    const handleDragStart = useCallback((e, subjectId) => { setDraggedSubjectId(subjectId); e.dataTransfer.effectAllowed = 'move'; }, []);
    const handleDragOver = useCallback((e, containerType) => {
        e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverContainer(containerType);
    }, []);
    const handleDragLeave = useCallback((e, containerType) => { if (dragOverContainer === containerType) { setDragOverContainer(null); } }, [dragOverContainer]);
    const handleDrop = useCallback((e, targetContainer) => {
        e.preventDefault();
        if (!draggedSubjectId) return;
        const subjectBeingMoved = getSubjectById(subjects, draggedSubjectId);
        if (!subjectBeingMoved) return;
        const currentlyQualified = qualifiedIds.has(draggedSubjectId);
        if (targetContainer === 'qualified' && !currentlyQualified) {
            setQualifiedIds(prev => new Set(prev).add(draggedSubjectId));
            setAvailableSubjects(prev => prev.filter(s => s.id !== draggedSubjectId));
        } else if (targetContainer === 'available' && currentlyQualified) {
            setQualifiedIds(prev => { const next = new Set(prev); next.delete(draggedSubjectId); return next; });
            setAvailableSubjects(prev => [...prev, subjectBeingMoved].sort((a, b) => a.name.localeCompare(b.name)));
        }
        setDraggedSubjectId(null);
        setDragOverContainer(null);
    }, [draggedSubjectId, qualifiedIds, subjects]);
    const handleDragEnd = useCallback(() => { setDraggedSubjectId(null); setDragOverContainer(null); }, []);
    const handleSave = () => { if (typeof onSaveQualifications === 'function') { onSaveQualifications(Array.from(qualifiedIds)); } onClose(); };

    // Show loading state
    if (isLoadingSubjects) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title={`Edit Subject Qualifications for ${teacherName || 'Teacher'}`}>
                <div className="flex justify-center items-center h-64">
                    <div className="text-gray-500">Loading subjects...</div>
                </div>
            </Modal>
        );
    }

    // Show error state
    if (subjectsError) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title={`Edit Subject Qualifications for ${teacherName || 'Teacher'}`}>
                <div className="flex flex-col items-center justify-center h-64">
                    <div className="text-red-500 mb-4">Error loading subjects: {subjectsError}</div>
                    <Button variant="primary" onClick={() => window.location.reload()}>
                        Retry
                    </Button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Edit Subject Qualifications for ${teacherName || 'Teacher'}`}>
            <div className="flex flex-col h-full max-h-[calc(80vh-8rem)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow min-h-0">

                    {/* Column 1: Available Subjects */}
                    <div className="flex flex-col space-y-2 overflow-hidden">
                        <h3 className="text-md font-semibold text-gray-700 flex-shrink-0">Available Subjects</h3>
                        <Input
                            type="text"
                            placeholder="Filter available subjects..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="text-sm py-1 flex-shrink-0"
                        />
                        <Card
                            className={`flex-grow p-2 border-2 ${dragOverContainer === 'available' ? 'border-blue-500 bg-blue-50' : 'border-dashed border-gray-300'} overflow-y-auto`}
                            onDragOver={(e) => handleDragOver(e, 'available')}
                            onDrop={(e) => handleDrop(e, 'available')}
                            onDragLeave={(e) => handleDragLeave(e, 'available')}
                            onDragEnd={handleDragEnd}
                        >
                            <div className="min-h-[50px]">
                                {filteredAvailableSubjects.length > 0 ? (
                                    filteredAvailableSubjects.map(subject => (
                                        <SubjectItem key={subject.id} subject={subject} onDragStart={handleDragStart} />
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500 italic text-center py-4">
                                        {filterText ? 'No matching subjects.' : 'All subjects assigned.'}
                                    </p>
                                )}
                            </div>
                        </Card>
                    </div>

                    {/* Column 2: Qualified Subjects */}
                    <div className="flex flex-col space-y-2 overflow-hidden">
                        <h3 className="text-md font-semibold text-gray-700 flex-shrink-0">Qualified Subjects ({qualifiedSubjectObjects.length})</h3>
                        <Card
                            className={`flex-grow p-2 border-2 ${dragOverContainer === 'qualified' ? 'border-blue-500 bg-blue-50' : 'border-dashed border-gray-300'} overflow-y-auto`}
                            onDragOver={(e) => handleDragOver(e, 'qualified')}
                            onDrop={(e) => handleDrop(e, 'qualified')}
                            onDragLeave={(e) => handleDragLeave(e, 'qualified')}
                            onDragEnd={handleDragEnd}
                        >
                            <div className="min-h-[50px]">
                                {qualifiedSubjectObjects.length > 0 ? (
                                    qualifiedSubjectObjects.map(subject => (
                                        <SubjectItem key={subject.id} subject={subject} onDragStart={handleDragStart} />
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500 italic text-center py-4">Drag subjects here</p>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Actions section - fixed at bottom */}
                <div className="mt-4 flex justify-end space-x-3 border-t pt-4 flex-shrink-0">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>Save Qualifications</Button>
                </div>
            </div>
        </Modal>
    );
}

export default SubjectQualificationModal;